import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Row, Col, Card, CardBody, Button } from "reactstrap";
import moment from "moment";
import { toast } from "react-toastify";

import arrow_btn from "../../assets/images/icon/arrow-btn.svg";

import {
  getEnquiryDetailAPI,
  replyEnquiryAPI,
  closeEnquiryAPI,
} from "../../helpers/backend_helper";
import { checkPermissionStatus } from "helpers/utils";
import { useTranslation } from "react-i18next";

const EnquiryDetail = props => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [enquiry, setEnquiry] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  /* =========================
     LOAD ENQUIRY DETAIL
  ========================== */
  const load = async () => {
    try {
      const res = await getEnquiryDetailAPI(id);

      // supports both response styles
      if (res?.data?.id) {
        setEnquiry(res.data);
      } else if (res?.data?.data?.id) {
        setEnquiry(res.data.data);
      } else {
        setEnquiry(null);
      }
    } catch (err) {
      console.error("Enquiry detail error:", err);
      setEnquiry(null);
    }
  };

  useEffect(() => {
    load();
    document.title = "NeuroTal - Enquiry Detail";
  }, []);

  /* =========================
     SEND REPLY
  ========================== */
  const sendReply = async () => {
    if (!message.trim()) return;

    try {
      setLoading(true);
      const res = await replyEnquiryAPI(id, message);

      if (res?.status) {
        toast.success(t("Enquiry.Reply sent"));
        setMessage("");
        await load(); // 🔥 reload enquiry
      }
    } catch (err) {
      toast.error(t("Enquiry.Reply failed"));
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     CLOSE ENQUIRY
  ========================== */
  const closeEnquiry = async () => {
    try {
      const res = await closeEnquiryAPI(id);
      if (res?.status) {
        toast.success(t("Enquiry.Closed successfully"));
        await load();
      }
    } catch (err) {
      toast.error(t("Enquiry.Close failed"));
    }
  };

  /* =========================
     LOADING STATE
  ========================== */
  if (!enquiry) {
    return (
      <div className="page-content text-center">
        <p>Loading enquiry details...</p>
      </div>
    );
  }

  /* =========================
     SORT MESSAGES
  ========================== */
  const messages = [...(enquiry.enquiry_messages || [])].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );

  /* =========================
     RENDER
  ========================== */
  return (
    <div className="page-content">
      {/* Header */}
      <div className="joblist-container create-vacancy">
        <Row className="mb-3 align-items-center">
          <Col lg="6">
            <Link to="/enquiries">
              <h5 className="text-uppercase heading-one">
                <img src={arrow_btn} alt="back" />
                {t("Enquiry.Enquiry Detail")}
              </h5>
            </Link>
          </Col>
        </Row>
      </div>

      {/* Enquiry Info */}
      <Card className="mb-3">
        <CardBody>
          <Row>
            <Col lg="6">
              <p>
                <strong>{t("Enquiry.Ticket No")}:</strong> {enquiry.ticket_no}
              </p>
              <p>
                <strong>{t("Enquiry.Subject")}:</strong> {enquiry.subject}
              </p>
              {/* 🔥 CREATED BY */}
              <p>
                <strong>{t("Enquiry.Created By")}:</strong>{" "}
                {enquiry.createdBy?.name || "-"}
                {enquiry.createdBy?.role?.name && (
                  <span className="text-muted ms-2">
                    ({enquiry.createdBy.role.name})
                  </span>
                )}
              </p>
              {/* 🔥 CLOSED BY */}
              <p>
                <strong>{t("Enquiry.Closed By")}:</strong>{" "}
                {enquiry.closedBy?.name || "-"}
                {enquiry.closedBy?.role?.name && (
                  <span className="text-muted ms-2">
                    ({enquiry.closedBy.role.name})
                  </span>
                )}
              </p>
            </Col>

            <Col lg="6">
              <p>
                <strong>{t("Enquiry.Status")}:</strong>{" "}
                <span
                  className={`badge ${
                    enquiry.status === "OPEN"
                      ? "bg-warning"
                      : enquiry.status === "IN_PROGRESS"
                      ? "bg-info"
                      : "bg-success"
                  }`}
                >
                  {enquiry.status}
                </span>
              </p>
              <p>
                <strong>{t("Enquiry.Created At")}:</strong>{" "}
                {moment(enquiry.created_at).format("DD MMM YYYY, HH:mm")}
              </p>
              <p>
                <strong>{t("Enquiry.Closed At")}:</strong>{" "}
                {enquiry?.closed_at
                  ? moment(enquiry.closed_at).format("DD MMM YYYY, HH:mm")
                  : "-"}
              </p>
            </Col>
          </Row>

          <hr />

          <p>
            <strong>{t("Enquiry.Description")}:</strong>
          </p>
          <p className="text-muted">{enquiry.description}</p>
        </CardBody>
      </Card>

      {/* Conversation */}
      <Card>
        <CardBody>
          <h5 className="mb-3">{t("Enquiry.Conversation")}</h5>

          <div className="conversation-wrapper professional-conversation">
            {messages.length === 0 && (
              <p className="text-muted text-center mb-3">
                No conversation yet. Start by replying below.
              </p>
            )}

            {messages.map((msg, index) => {
              const isAdmin =
                msg.sender?.Role?.alias === "admin" ||
                msg.sender?.Role?.alias === "super_admin";

              return (
                <div
                  key={index}
                  className={`conversation-row mb-4 ${
                    isAdmin ? "admin-reply" : "user-reply"
                  }`}
                >
                  <div className="conversation-card">
                    {/* Header */}
                    <div className="conversation-header d-flex justify-content-between">
                      <div>
                        <strong className="sender-name">
                          {msg.sender?.name || "System"}
                        </strong>
                        {msg.sender?.Role?.name && (
                          <span className="sender-role">
                            {msg.sender.Role.name}
                          </span>
                        )}
                      </div>

                      <small className="text-muted">
                        {moment(msg.created_at).format("DD MMM YYYY, HH:mm")}
                      </small>
                    </div>

                    {/* Message */}
                    <div className="conversation-body">{msg.message}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reply Box */}
          {enquiry.status !== "CLOSED" &&
            checkPermissionStatus("enquiries", "reply") && (
              <>
                <textarea
                  className="form-control mb-3"
                  rows="3"
                  placeholder={t("Enquiry.Type your reply")}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                />

                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    disabled={loading}
                    onClick={sendReply}
                  >
                    {t("Enquiry.Reply")}
                  </Button>

                  {checkPermissionStatus("enquiries", "close") && (
                    <Button color="danger" onClick={closeEnquiry}>
                      {t("Enquiry.Close Enquiry")}
                    </Button>
                  )}
                </div>
              </>
            )}
        </CardBody>
      </Card>
    </div>
  );
};

export default EnquiryDetail;
